/*
 * CHI-20 / CHI-27 / CHI-28: My Events ticket list + the ONE shared Stripe success path
 * for every internally paid event: /account/events?order=<id>&session_id={CHECKOUT_SESSION_ID}
 * (contract v4; built server-side from PUBLIC_SITE_URL).
 *
 * 1. events.verifyCheckoutReturn({orderId, sessionId}) (CHI-26) asks Stripe via the server
 *    whether the session is paid. unpaid -> "Payment not completed" + link back to the ticket page.
 * 2. paid -> "Finalizing your tickets…" and poll every pollMs (3s) for up to windowMs (60s)
 *    until the order is confirmed AND all its tickets exist; then render them together.
 *    Paused while the tab is hidden, resumed on focus; never two requests in flight.
 * 3. Window over -> exact CHI-28 copy, no ticket/QR, slow recheck every backgroundMs (15s).
 * Read-only: the client never creates tickets. No session_id/order -> the normal My Events view.
 */
import { useTicketingApiWiring } from "@/lib/ticketingApiProvider";
import { invalidateConfirmedTicketCache } from "@/lib/confirmedTicketCache";
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import QRCode from "qrcode";
import { CheckCircle2, LockKeyhole, Pencil, Ticket as TicketIcon, XCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  FINALIZING_BACKGROUND_INTERVAL_MS, FINALIZING_POLL_INTERVAL_MS, FINALIZING_WINDOW_MS,
  formatTicketRaffleNumber, getTicketingApi, SUPPORT_EMAIL, TICKETS_CANCELLED_PARAM, TICKETS_CANCELLED_VALUE, TICKETS_ORDER_PARAM, TICKETS_SESSION_PARAM, type MyTicket,
} from "@/lib/ticketingApi";
import { formatReceiptMoney, type TicketReceipt } from "@shared/ticketReceipt";
import { clearAllTicketDrafts } from "@/lib/ticketAttendees";
import { useServerNow } from "@/lib/serverClock";
import { notifyTicketsConfirmed } from "@/lib/ticketsConfirmed";
import EditGuestSlot from "./EditGuestSlot";

type Phase = "idle" | "checking" | "finalizing" | "timeout" | "paid" | "unpaid" | "not_found" | "under_review";

/** CHI-28 copy (exact). The support sentence is dropped when SUPPORT_EMAIL is unset. */
export const CHI28_TIMEOUT_COPY = "Your payment went through and your tickets are on the way. You'll get an email as soon as they're ready.";
export const PAYMENT_PENDING_COPY = "We're still waiting for payment confirmation. Your tickets will appear after payment is confirmed.";

/** CHI-26 UAT 1-2: what Stripe said, shown on every paid phase (finalizing, timeout, paid). */
export const PAYMENT_RECEIVED_COPY = "Payment received";
/** CHI-26 UAT 3: tampered / another buyer's session. Generic, no order details. */
export const ORDER_NOT_FOUND_COPY = "We couldn't find that order.";

const isNotFound = (error: unknown) => (error as { data?: { code?: string } } | null)?.data?.code === "NOT_FOUND";

export function dedupeTickets(list: MyTicket[]) {
  const seen = new Set<number>();
  return list.filter(t => (seen.has(t.ticketId) ? false : (seen.add(t.ticketId), true)));
}

/** QR from the owner-returned server token only. Never constructed, logged or persisted. */
export function TicketQr({ token, label, size = 200 }: { token: string; label: string; size?: number }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toString(token, { type: "svg", errorCorrectionLevel: "M", margin: 4 }).then(s => { if (live) setSvg(s); }, () => undefined);
    return () => { live = false; };
  }, [token]);
  return <div className="chi-ticket-qr" role="img" aria-label={label} data-testid="ticket-qr" style={{ width: size, height: size, maxWidth: "100%", background: "white", borderRadius: 8 }} dangerouslySetInnerHTML={svg ? { __html: svg } : undefined} />;
}

/** Component-scoped invitation skin; selectors are intentionally stronger than site-wide heading/span rules. */
export const TICKET_QR_CARD_CSS = `
.chi-member-ticket{box-sizing:border-box;font-family:system-ui,sans-serif;color:#322024}
.chi-member-ticket *{box-sizing:border-box}
.chi-member-ticket .chi-ticket-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.chi-ticket-group{display:grid;gap:12px;min-width:0}
.chi-ticket-group__header{overflow:hidden;border:1px solid #553147;border-radius:16px;background:#251628;color:#f1eaf0}
.chi-ticket-group__masthead{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 15px;background:#34172f}
.chi-ticket-group__brand{margin:0;color:#d8b977!important;font-size:.58rem!important;font-weight:750!important;letter-spacing:.14em;text-transform:uppercase}
.chi-ticket-group__heading{margin:2px 0 0!important;color:#fff3ed!important;font-family:'Space Grotesk',system-ui,sans-serif!important;font-size:1.05rem!important;font-weight:700!important;line-height:1.2!important}
.chi-ticket-group__count{flex:none;display:inline-flex;align-items:center;min-height:28px;padding:4px 9px;border:1px solid rgba(224,198,151,.2);border-radius:999px;background:rgba(255,255,255,.06);color:#e9dfd0;font-size:.7rem;font-weight:700;white-space:nowrap}
.chi-ticket-group__event{padding:11px 15px 12px}
.chi-ticket-group__event-title{margin:0;color:#f0edf4!important;font-family:'Space Grotesk',system-ui,sans-serif!important;font-size:1.02rem!important;font-weight:650!important;line-height:1.28!important;overflow-wrap:anywhere}
.chi-ticket-group__meta{margin:4px 0 0;color:#aaa9b7;font-size:.74rem;line-height:1.45;overflow-wrap:anywhere}
.chi-ticket-receipt{overflow:hidden;border:1px solid #473149;border-radius:14px;background:#111722;color:#e9e5ec}
.chi-ticket-receipt__head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:13px 15px;border-bottom:1px solid #34303d;background:#211526}
.chi-ticket-receipt__eyebrow{margin:0;color:#d9ba80;font-size:.59rem;font-weight:750;letter-spacing:.14em;text-transform:uppercase}
.chi-ticket-receipt__order{margin:3px 0 0!important;color:#f5edf3!important;font-family:'Space Grotesk',system-ui,sans-serif!important;font-size:.94rem!important;font-weight:700!important;line-height:1.3!important}
.chi-ticket-receipt__paid{flex:none;padding:4px 8px;border:1px solid #285841;border-radius:999px;background:#183a2d;color:#8bd8a5;font-size:.63rem;font-weight:750;letter-spacing:.05em}
.chi-ticket-receipt__date{margin:4px 0 0;color:#b1a5b3;font-size:.7rem;line-height:1.35}
.chi-ticket-receipt__lines{margin:0;padding:4px 15px 0}
.chi-ticket-receipt__row{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:10px 0;border-bottom:1px solid #2d303b}
.chi-ticket-receipt__row dt{min-width:0;color:#bdb9c3;font-size:.74rem;line-height:1.4}
.chi-ticket-receipt__row dt small{display:block;margin-top:2px;color:#898b99;font-size:.67rem}
.chi-ticket-receipt__row dd{flex:none;margin:0;color:#e9e6ed;font-size:.74rem;font-weight:650;line-height:1.4;text-align:right;overflow-wrap:anywhere}
.chi-ticket-receipt__total{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 15px 14px;background:#191522}
.chi-ticket-receipt__total-label{display:block;color:#f1e7f0;font-size:.75rem;font-weight:750}
.chi-ticket-receipt__no-charge{display:block;margin-top:3px;color:#c5b1c4;font-size:.66rem}
.chi-ticket-receipt__amount{color:#f1d18f;font-family:'Space Grotesk',system-ui,sans-serif;font-size:1.16rem;font-weight:750;white-space:nowrap}
.chi-ticket-receipt__missing{margin:10px 0 0;color:#b9b2c1;font-size:.78rem;line-height:1.5}
.chi-member-ticket .chi-member-ticket__mini{width:100%;overflow:hidden;border:1px solid #2b3142;border-radius:16px;background:#141a2a;color:#e8e7ed;padding:12px}
.chi-member-ticket .chi-member-ticket__mini-top{display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:10px;min-width:0}
.chi-member-ticket .chi-member-ticket__ticket-icon{display:grid;width:34px;height:34px;place-items:center;border:1px solid #50314a;border-radius:9px;background:#34172f;color:#d7b36e}
.chi-member-ticket .chi-member-ticket__identity{min-width:0}
.chi-member-ticket .chi-member-ticket__name{margin:0!important;color:#f3f1f4!important;font-family:'Space Grotesk',system-ui,sans-serif!important;font-size:.94rem!important;font-weight:650!important;line-height:1.25!important;overflow-wrap:anywhere}
.chi-member-ticket .chi-member-ticket__email{margin:3px 0 0;color:#a6a7b4;font-size:.72rem;line-height:1.3;overflow-wrap:anywhere;word-break:break-word}
.chi-member-ticket .chi-member-ticket__status{display:inline-flex;align-items:center;justify-content:center;min-height:27px;padding:4px 8px;border:1px solid #285841;border-radius:999px;background:#183a2d;color:#8bd8a5;font-size:.68rem;font-weight:700;line-height:1.15;white-space:nowrap}
.chi-member-ticket .chi-member-ticket__status[data-state="inactive"]{border-color:#514650;background:#29242e;color:#d7c3ce}
.chi-member-ticket .chi-member-ticket__divider{height:1px;margin:11px 0 10px;border:0;background:#303444}
.chi-member-ticket .chi-member-ticket__mini-lower{display:grid;grid-template-columns:minmax(0,1fr) 84px;align-items:center;gap:12px;min-width:0}
.chi-member-ticket .chi-member-ticket__admission{min-width:0}
.chi-member-ticket .chi-member-ticket__type{margin:0;color:#aaa8b5;font-size:.59rem;font-weight:750;letter-spacing:.09em;line-height:1.35;text-transform:uppercase;overflow-wrap:anywhere}
.chi-member-ticket .chi-member-ticket__entry{display:flex;align-items:flex-start;gap:5px;margin:5px 0 0;color:#d2c5a2;font-size:.69rem;line-height:1.35}
.chi-member-ticket .chi-member-ticket__entry svg{flex:none;margin-top:1px;color:#c9a961}
.chi-member-ticket .chi-member-ticket__raffle{margin:6px 0 0;color:#d7b36e;font-size:.73rem;font-weight:800;letter-spacing:.08em}
.chi-member-ticket .chi-member-ticket__raffle strong{color:#f4e3b8}
.chi-member-ticket .chi-member-ticket__mini-qr{display:grid;place-items:center;width:84px;height:84px;padding:5px;border:1px solid #e2e1e6;border-radius:9px;background:#fff}
.chi-member-ticket .chi-member-ticket__mini-qr .chi-ticket-qr{width:74px!important;height:74px!important}
.chi-member-ticket .chi-member-ticket__inactive-qr{display:flex;width:84px;min-height:84px;flex-direction:column;align-items:center;justify-content:center;gap:4px;padding:7px;border:1px solid #46414b;border-radius:9px;background:#1c202d;color:#d2bbc2;text-align:center}
.chi-member-ticket .chi-member-ticket__inactive-qr svg{width:18px;height:18px;color:#d49b9b}
.chi-member-ticket .chi-member-ticket__inactive-qr strong{font-size:.64rem;line-height:1.2}
.chi-member-ticket .chi-member-ticket__inactive-qr span{font-size:.58rem;line-height:1.2}
.chi-member-ticket .chi-member-ticket__actions{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:7px}
.chi-member-ticket .chi-member-ticket__view{min-height:44px;border:1px solid #4b5265;border-radius:9px;padding:8px 11px;background:#202638;color:#f1edf3;font:700 .76rem 'Space Grotesk',system-ui,sans-serif;cursor:pointer}
.chi-member-ticket .chi-member-ticket__view:hover{border-color:#9b7a4e;background:#282d3b}
.chi-member-ticket .chi-member-ticket__edit-disclosure{margin-top:6px}
.chi-member-ticket .chi-member-ticket__edit-disclosure summary{display:flex;align-items:center;gap:6px;width:max-content;min-height:44px;padding:5px 2px;color:#c8b8cc;font-size:.76rem;font-weight:650;cursor:pointer;list-style:none}
.chi-member-ticket .chi-member-ticket__edit-disclosure summary::-webkit-details-marker{display:none}
.chi-member-ticket .chi-member-ticket__edit-disclosure summary:focus-visible,.chi-member-ticket .chi-member-ticket__view:focus-visible{outline:3px solid #d7b36e;outline-offset:2px}
.chi-member-ticket .chi-member-ticket__edit{padding:8px 0 0;color:#d8c7d7;font-size:.8rem}
.chi-member-ticket .chi-ticket-invitation__full{width:min(100%,620px);margin:0 auto;overflow:hidden;border:1px solid #a9884d;border-radius:14px;background:#fff9ea;color:#3b2020;box-shadow:0 18px 50px rgba(0,0,0,.3)}
.chi-member-ticket .chi-ticket-invitation__hero{min-height:146px;display:flex;flex-direction:column;justify-content:space-between;padding:20px 24px;background:linear-gradient(90deg,rgba(55,5,14,.89),rgba(91,8,19,.74)),url('/media/tickets/red-gold-banquet.webp') center 46%/cover;color:#f8e9bd}
.chi-member-ticket .chi-ticket-invitation__brand{margin:0;font-family:Georgia,serif;font-size:1rem;letter-spacing:.17em;text-transform:uppercase}
.chi-member-ticket .chi-ticket-invitation__tagline{margin:18px 0 0;font-family:Georgia,serif;font-size:clamp(1.25rem,4vw,1.8rem);line-height:1.1}
.chi-member-ticket .chi-ticket-invitation__body{padding:20px 24px 18px}
.chi-member-ticket .chi-ticket-invitation__eyebrow{margin:0 0 7px;color:#7e3037;font-size:.67rem;font-weight:750;letter-spacing:.16em;text-transform:uppercase}
.chi-member-ticket .chi-ticket-invitation__title{margin:0;color:#3e2021;font-family:Georgia,serif;font-size:clamp(1.3rem,4vw,1.85rem);font-weight:500;line-height:1.16;overflow-wrap:anywhere}
.chi-member-ticket .chi-ticket-invitation__details{display:block;margin:17px 0 0;color:#796a56;font-size:.84rem;line-height:1.55;overflow-wrap:anywhere}
.chi-member-ticket .chi-ticket-invitation__rule{margin:18px 0;border:0;border-top:1px dashed #d6c9ae}
.chi-member-ticket .chi-ticket-invitation__reserved-label{margin:0 0 4px;color:#8b3940;font-size:.65rem;font-weight:750;letter-spacing:.17em;text-transform:uppercase}
.chi-member-ticket .chi-ticket-invitation__reserved{margin:0;color:#432124;font-family:Georgia,serif;font-size:1.2rem;line-height:1.25;overflow-wrap:anywhere}
.chi-member-ticket .chi-ticket-invitation__status{display:inline-flex;margin-top:9px;padding:4px 9px;border:1px solid #d6c397;border-radius:999px;color:#6e4d23;background:#f4ebd7;font-size:.7rem;font-weight:700}
.chi-member-ticket .chi-ticket-invitation__status[data-state="inactive"]{border-color:#deb9a5;color:#7b3338;background:#f7e6d9}
.chi-member-ticket .chi-ticket-invitation__code-row{display:grid;grid-template-columns:minmax(0,280px) minmax(0,1fr);gap:18px;align-items:center;margin-top:18px}
.chi-member-ticket .chi-ticket-invitation__code-row--empty{grid-template-columns:1fr}
.chi-member-ticket .chi-ticket-invitation__qr{display:grid;place-items:center;min-width:0;padding:8px;background:#fff;border-radius:8px}
.chi-member-ticket .chi-ticket-invitation__qr .chi-ticket-qr{width:min(280px,100%)!important;height:auto!important;aspect-ratio:1}
.chi-member-ticket .chi-ticket-invitation__arrive{margin:0;color:#48272a;font-family:Georgia,serif;font-size:1.2rem}
.chi-member-ticket .chi-ticket-invitation__instructions{margin:7px 0 0;color:#756b61;font-size:.79rem;line-height:1.5}
.chi-member-ticket .chi-ticket-invitation__raffle{margin:13px 0 0;color:#8a303b;font-size:.84rem;font-weight:800;letter-spacing:.16em}
.chi-member-ticket .chi-ticket-invitation__inactive{margin:0;padding:14px;border:1px solid #ddc9a5;border-radius:8px;color:#683337;background:#f7edda;font-size:.85rem;line-height:1.5}
.chi-member-ticket .chi-ticket-invitation__privacy{margin:15px 0 0;color:#796f61;font-size:.72rem;line-height:1.45}
.chi-member-ticket-dialog[data-slot="dialog-content"]{max-width:min(680px,calc(100% - 20px));max-height:calc(100dvh - 24px);overflow:auto;padding:20px;background:#17131a;border-color:#8e6c45;color:#f5ead1}
.chi-member-ticket-dialog [data-slot="dialog-close"]{top:8px;right:8px;display:grid;width:44px;height:44px;place-items:center;border-radius:8px}
.chi-member-ticket .chi-member-ticket-dialog__title{margin:0 36px 0 0;color:#f5ead1;font-family:Georgia,serif;font-size:1.15rem;line-height:1.3}
.chi-member-ticket .chi-member-ticket-dialog__description{margin:5px 0 0;color:#c8bda9}
.chi-member-ticket--large{display:flex;flex-direction:column;align-items:center;gap:0;list-style:none;padding:0}
.chi-member-ticket--large .chi-ticket-invitation__full{width:min(100%,680px)}
.chi-member-ticket--large .chi-ticket-invitation__qr .chi-ticket-qr{width:min(280px,100%)!important;height:auto!important}
@media(max-width:380px){.chi-member-ticket .chi-member-ticket__mini{padding:10px}.chi-member-ticket .chi-member-ticket__mini-top{grid-template-columns:32px minmax(0,1fr) auto;gap:8px}.chi-member-ticket .chi-member-ticket__ticket-icon{width:32px;height:32px}.chi-member-ticket .chi-member-ticket__mini-lower{grid-template-columns:minmax(0,1fr) 76px;gap:8px}.chi-member-ticket .chi-member-ticket__mini-qr,.chi-member-ticket .chi-member-ticket__inactive-qr{width:76px;height:76px;min-height:76px}.chi-member-ticket .chi-member-ticket__mini-qr .chi-ticket-qr{width:66px!important;height:66px!important}.chi-member-ticket .chi-member-ticket__type{font-size:.55rem}.chi-member-ticket .chi-member-ticket__status{font-size:.63rem;padding:4px 6px}}
@media(max-width:340px){.chi-member-ticket .chi-member-ticket__email{font-size:.67rem}.chi-member-ticket .chi-member-ticket__status{max-width:80px;white-space:normal;text-align:center}.chi-member-ticket .chi-member-ticket__view{padding:8px 9px}}
@media(prefers-reduced-motion:reduce){.chi-member-ticket *{scroll-behavior:auto!important;transition:none!important;animation:none!important}}
`;

function ticketStatus(ticket: MyTicket) {
  if (ticket.checkedInAt) return { label: "Checked in", state: "inactive" as const, explanation: "This ticket has already been checked in. Its QR code is no longer active." };
  if (ticket.status === "active") {
    return ticket.qrToken
      ? { label: "Active · single-use QR", state: "active" as const, explanation: "Present this QR code at the door. It is valid for one check-in." }
      : { label: "Active · QR unavailable", state: "inactive" as const, explanation: "The QR code is not available for this ticket right now." };
  }
  const labels: Record<string, string> = { revoked: "Revoked", refunded: "Refunded", suspended: "Suspended", cancelled: "Cancelled" };
  return { label: labels[ticket.status] ?? "Inactive", state: "inactive" as const, explanation: `This ticket is ${labels[ticket.status]?.toLowerCase() ?? "inactive"} and is not valid for entry.` };
}

function ticketDateTime(ticket: MyTicket, details?: { date?: string; time?: string; venue?: string }) {
  if (details && [details.date, details.time, details.venue].some(Boolean)) return [details.date, details.time, details.venue].filter(Boolean);
  if (!ticket.eventStartsAt) return ["Date to be announced"];
  const instant = new Date(ticket.eventStartsAt);
  if (!Number.isFinite(instant.getTime())) return ["Date to be announced"];
  return [
    new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(instant),
    new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(instant),
  ];
}

function TicketInvitation({ ticket, eventTitle, details, size = 280 }: {
  ticket: MyTicket; eventTitle: string; details?: { date?: string; time?: string; venue?: string }; size?: number;
}) {
  const status = ticketStatus(ticket);
  const validQr = ticket.status === "active" && !ticket.checkedInAt && Boolean(ticket.qrToken);
  const raffleNumber = formatTicketRaffleNumber(ticket.ticketId);
  const metadata = ticketDateTime(ticket, details);
  return (
    <article className="chi-ticket-invitation__full">
      <header className="chi-ticket-invitation__hero">
        <p className="chi-ticket-invitation__brand">The CHI Alliance</p>
        <p className="chi-ticket-invitation__tagline">An invitation to connect.</p>
      </header>
      <div className="chi-ticket-invitation__body">
        <p className="chi-ticket-invitation__eyebrow">Official admission · Admit one</p>
        <h2 className="chi-ticket-invitation__title">{eventTitle}</h2>
        <div className="chi-ticket-invitation__details" data-testid="ticket-details">
          {metadata.map((detail, index) => <Fragment key={`${index}-${detail}`}>{index > 0 ? " · " : null}<span>{detail}</span></Fragment>)}
        </div>
        <hr className="chi-ticket-invitation__rule" />
        <p className="chi-ticket-invitation__reserved-label">Reserved for</p>
        <p className="chi-ticket-invitation__reserved">{ticket.attendeeName}</p>
        <span className="chi-ticket-invitation__status" data-state={status.state} data-testid="ticket-status">{status.label}</span>
        <div className={`chi-ticket-invitation__code-row${validQr ? "" : " chi-ticket-invitation__code-row--empty"}`}>
          {validQr ? (
            <div className="chi-ticket-invitation__qr">
              <TicketQr token={ticket.qrToken!} size={size} label={`Active QR code for ${ticket.attendeeName}'s ticket`} />
            </div>
          ) : (
            <p className="chi-ticket-invitation__inactive" role="status">{status.explanation}</p>
          )}
          <div>
            <p className="chi-ticket-invitation__arrive">{validQr ? "Your arrival, made simple." : status.label}</p>
            {validQr && <p className="chi-ticket-invitation__instructions">Present this QR code at the door. Valid for one check-in only.</p>}
            <p className="chi-ticket-invitation__raffle"><span>Raffle #</span> {raffleNumber}</p>
          </div>
        </div>
        <p className="chi-ticket-invitation__privacy"><LockKeyhole size={12} aria-hidden="true" /> Keep your ticket private. A copy cannot be used after check-in.</p>
      </div>
    </article>
  );
}

function ticketGroupMetadata(ticket: MyTicket, details?: { date?: string; time?: string; venue?: string }) {
  const provided = [details?.date, details?.time, details?.venue].filter((value): value is string => Boolean(value));
  if (provided.length) return provided;
  if (!ticket.eventStartsAt) return [];
  const instant = new Date(ticket.eventStartsAt);
  if (!Number.isFinite(instant.getTime())) return [];
  return [
    new Intl.DateTimeFormat(undefined, { dateStyle: "full", timeZone: "UTC" }).format(instant),
    new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone: "UTC", timeZoneName: "short" }).format(instant),
  ];
}

/** Shared truthful event context for the event tab and receipt ticket groups. */
export function TicketGroupHeader({ tickets, eventTitle, details }: {
  tickets: MyTicket[]; eventTitle?: string; details?: { date?: string; time?: string; venue?: string };
}) {
  if (tickets.length === 0) return null;
  const metadata = ticketGroupMetadata(tickets[0]!, details);
  return (
    <header className="chi-ticket-group__header">
      <div className="chi-ticket-group__masthead">
        <div>
          <p className="chi-ticket-group__brand">The CHI Alliance</p>
          <h2 className="chi-ticket-group__heading">My Event Tickets</h2>
        </div>
        <span className="chi-ticket-group__count">{tickets.length} {tickets.length === 1 ? "Ticket" : "Tickets"}</span>
      </div>
      {(eventTitle || metadata.length > 0) && (
        <div className="chi-ticket-group__event">
          {eventTitle && <h3 className="chi-ticket-group__event-title">{eventTitle}</h3>}
          {metadata.length > 0 && <p className="chi-ticket-group__meta">{metadata.join(" · ")}</p>}
        </div>
      )}
    </header>
  );
}

function receiptDateLabel(paidAt: string | null) {
  if (!paidAt) return null;
  const instant = new Date(paidAt);
  return Number.isFinite(instant.getTime())
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(instant)
    : null;
}

function TicketOrderReceipt({ receipt }: { receipt: TicketReceipt }) {
  const paidAt = receiptDateLabel(receipt.paidAt);
  const unitPrice = formatReceiptMoney(receipt.unitAmountCents, receipt.currency);
  const rows: Array<{ label: string; value: string; note?: string }> = [
    {
      label: `${receipt.quantity} ${receipt.quantity === 1 ? "ticket" : "tickets"}`,
      value: `${receipt.quantity} × ${unitPrice}`,
      note: receipt.pricingLabel,
    },
    { label: "Subtotal", value: formatReceiptMoney(receipt.subtotalCents, receipt.currency) },
  ];
  if (receipt.promotionCode) rows.push({ label: "Promotion code", value: receipt.promotionCode });
  if (receipt.discountCents !== 0) rows.push({
    label: receipt.promotionCode ? "Coupon discount" : "Discount",
    value: `−${formatReceiptMoney(Math.abs(receipt.discountCents), receipt.currency)}`,
  });
  if (receipt.additionalChargesCents !== 0) rows.push({
    label: "Additional charges",
    value: formatReceiptMoney(receipt.additionalChargesCents, receipt.currency),
  });
  return (
    <article className="chi-ticket-receipt" data-testid="ticket-order-receipt" data-order-id={receipt.orderId} aria-label={`Order receipt ${receipt.orderId}`}>
      <header className="chi-ticket-receipt__head">
        <div>
          <p className="chi-ticket-receipt__eyebrow">Order receipt</p>
          <h3 className="chi-ticket-receipt__order">Order #{receipt.orderId}</h3>
          {paidAt && <p className="chi-ticket-receipt__date"><time dateTime={receipt.paidAt ?? undefined}>{paidAt}</time></p>}
        </div>
        <span className="chi-ticket-receipt__paid">CONFIRMED</span>
      </header>
      <dl className="chi-ticket-receipt__lines">
        {rows.map(row => (
          <div className="chi-ticket-receipt__row" key={row.label}>
            <dt>{row.label}{row.note && <small>{row.note}</small>}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      <div className="chi-ticket-receipt__total">
        <div>
          <span className="chi-ticket-receipt__total-label">Total paid</span>
          {receipt.totalCents === 0 && <span className="chi-ticket-receipt__no-charge">No charge</span>}
        </div>
        <strong className="chi-ticket-receipt__amount">{formatReceiptMoney(receipt.totalCents, receipt.currency)}</strong>
      </div>
    </article>
  );
}

/** The compact card expands into the same real invitation; the print variant remains one ticket per page. */
export function TicketQrCard({ ticket, eventTitle, now, onGuestUpdated, variant = "default", details, allowGuestEdits = true, includeStyles = true }: {
  ticket: MyTicket; eventTitle?: string; now: number; onGuestUpdated: () => void; variant?: "default" | "large"; details?: { date?: string; time?: string; venue?: string }; allowGuestEdits?: boolean; includeStyles?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const large = variant === "large";
  const startsAt = ticket.eventStartsAt ? Date.parse(ticket.eventStartsAt) : NaN;
  const beforeStart = !Number.isNaN(startsAt) && now < startsAt;
  const title = eventTitle ?? ticket.eventSlug;
  const status = ticketStatus(ticket);
  const validQr = ticket.status === "active" && !ticket.checkedInAt && Boolean(ticket.qrToken);
  const raffleNumber = formatTicketRaffleNumber(ticket.ticketId);
  const rootClass = large ? "chi-member-ticket chi-member-ticket--large chi-print-ticket" : "chi-member-ticket chi-member-ticket--mini";

  if (large) {
    return (
      <li className={rootClass} data-testid="ticket-card" data-variant="large" style={{ listStyle: "none" }}>
        {includeStyles && <style>{TICKET_QR_CARD_CSS}</style>}
        <TicketInvitation ticket={ticket} eventTitle={title} details={details} size={280} />
        {allowGuestEdits && beforeStart && !ticket.claimedByViewer && (
          <div data-slot="edit-guest">
            <EditGuestSlot ticketId={ticket.ticketId} guestName={ticket.attendeeName} guestEmail={ticket.attendeeEmail} onGuestUpdated={onGuestUpdated} />
          </div>
        )}
      </li>
    );
  }

  return (
    <article className={rootClass} data-testid="ticket-card" data-variant="default">
      {includeStyles && <style>{TICKET_QR_CARD_CSS}</style>}
      <section className="chi-member-ticket__mini" aria-label={`Ticket for ${ticket.attendeeName}`}>
        <div className="chi-member-ticket__mini-top">
          <span className="chi-member-ticket__ticket-icon" aria-hidden="true"><TicketIcon size={18} /></span>
          <div className="chi-member-ticket__identity">
            <h3 className="chi-member-ticket__name">{ticket.attendeeName}</h3>
            {ticket.attendeeEmail && <p className="chi-member-ticket__email">{ticket.attendeeEmail}</p>}
          </div>
          <span className="chi-member-ticket__status" data-state={validQr ? "active" : "inactive"} data-testid="ticket-status">
            {validQr && <CheckCircle2 size={13} aria-hidden="true" />}{validQr ? "Active" : status.label}
          </span>
        </div>
        <hr className="chi-member-ticket__divider" />
        <div className="chi-member-ticket__mini-lower">
          <div className="chi-member-ticket__admission">
            <p className="chi-member-ticket__type">General admission · Ticket {ticket.ticketId}</p>
            {validQr ? (
              <p className="chi-member-ticket__entry"><LockKeyhole size={12} aria-hidden="true" /> Valid for one entry</p>
            ) : (
              <p className="chi-member-ticket__entry" role="status">{status.explanation}</p>
            )}
            <p className="chi-member-ticket__raffle">Raffle # <strong>{raffleNumber}</strong></p>
            <div className="chi-member-ticket__actions">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button type="button" className="chi-member-ticket__view">View ticket</button>
                </DialogTrigger>
                <DialogContent className="chi-member-ticket chi-member-ticket-dialog">
                  <DialogTitle className="chi-member-ticket-dialog__title">Your full-size ticket</DialogTitle>
                  <DialogDescription className="chi-member-ticket-dialog__description">Show the QR code at the event entrance. Your raffle number is shown on the ticket.</DialogDescription>
                  <TicketInvitation ticket={ticket} eventTitle={title} details={details} size={280} />
                </DialogContent>
              </Dialog>
            </div>
            {allowGuestEdits && beforeStart && !ticket.claimedByViewer && (
              <details className="chi-member-ticket__edit-disclosure" data-slot="edit-guest">
                <summary><Pencil size={14} aria-hidden="true" /> Edit guest</summary>
                <div className="chi-member-ticket__edit">
                  <EditGuestSlot ticketId={ticket.ticketId} guestName={ticket.attendeeName} guestEmail={ticket.attendeeEmail} onGuestUpdated={onGuestUpdated} />
                </div>
              </details>
            )}
          </div>
          <div className="chi-member-ticket__mini-qr">
            {validQr ? (
              <TicketQr token={ticket.qrToken!} size={74} label={`QR code for ${ticket.attendeeName}'s ticket, ID ${ticket.ticketId}`} />
            ) : (
              <div className="chi-member-ticket__inactive-qr" role="status" aria-label={status.explanation}>
                <XCircle aria-hidden="true" />
                <strong>{status.label}</strong>
                <span>QR inactive</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </article>
  );
}

export default function MyTickets({ titleForSlug, detailsForSlug, readyContent, supportEmail = SUPPORT_EMAIL, pollMs = FINALIZING_POLL_INTERVAL_MS, windowMs = FINALIZING_WINDOW_MS, backgroundMs = FINALIZING_BACKGROUND_INTERVAL_MS, allowGuestEdits = true, authenticatedViewer = false }: {
  titleForSlug?: (slug: string) => string | undefined; detailsForSlug?: (slug: string) => { date?: string; time?: string; venue?: string } | undefined; readyContent?: (tickets: MyTicket[]) => ReactNode; supportEmail?: string | null; pollMs?: number; windowMs?: number; backgroundMs?: number; allowGuestEdits?: boolean; authenticatedViewer?: boolean;
}) {
  const trpcUtils = useTicketingApiWiring();
  const [{ sessionId, returnOrderId, checkoutCancelled }] = useState(() => {
    const q = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const order = q.get(TICKETS_ORDER_PARAM);
    const sid = q.get(TICKETS_SESSION_PARAM);
    return {
      sessionId: order && /^\d+$/.test(order) && sid ? sid : null,
      returnOrderId: order && /^\d+$/.test(order) && sid ? Number(order) : null,
      checkoutCancelled: q.get(TICKETS_CANCELLED_PARAM) === TICKETS_CANCELLED_VALUE,
    };
  });
  const trpcUtilsRef = useRef(trpcUtils);
  trpcUtilsRef.current = trpcUtils;
  const [phase, setPhase] = useState<Phase>(sessionId ? "checking" : "idle");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [tickets, setTickets] = useState<MyTicket[]>([]);
  const [receipts, setReceipts] = useState<TicketReceipt[]>([]);
  const [serverNow, setServerNow] = useState<string | undefined>(undefined);
  const [backHref, setBackHref] = useState("/calendar-of-events");
  const live = useRef(true);
  const now = useServerNow(serverNow, 30_000);

  const load = useCallback(async () => {
    try {
      const res = await getTicketingApi().myTickets();
      if (!live.current) return null;
      setTickets(dedupeTickets(res.tickets));
      setReceipts(res.receipts ?? []);
      setServerNow(res.serverNow);
      return res;
    } catch {
      if (live.current) setReceipts([]);
      return null;
    }
  }, []);

  useEffect(() => {
    live.current = true;
    if (!sessionId) {
      void load();
      return () => { live.current = false; };
    }
    const api = getTicketingApi();
    let orderId: number | null = null;
    let started = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let done = false;
    let mode: "window" | "background" = "window";

    const schedule = () => {
      if (done || !live.current || timer) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return; // resumes on focus
      if (mode === "window" && Date.now() - started >= windowMs) { mode = "background"; setPhase("timeout"); }
      timer = setTimeout(() => { timer = undefined; void check(); }, mode === "window" ? pollMs : backgroundMs);
    };

    /** Confirmed = order paid, its ticketCount > 0, and every one of those tickets is in myTickets. */
    const check = async () => {
      if (done || inFlight || !live.current || orderId === null) return;
      inFlight = true;
      try {
        const st = await api.ticketCheckoutStatus({ orderId, sessionId });
        if (!live.current) return;
        if (st.status === "paid") {
          setPaymentConfirmed(true);
          if (st.needsReview && st.ticketCount === 0) {
            done = true;
            setPhase("under_review");
            return;
          }
          setPhase(mode === "window" ? "finalizing" : "timeout");
        }
        if (st.status === "paid" && st.ticketCount > 0) {
          const res = await api.myTickets();
          const mine = dedupeTickets(res.tickets);
          const forOrder = mine.filter(t => t.orderId === orderId && t.status === "active" && Boolean(t.qrToken));
          if (forOrder.length >= Math.max(st.ticketCount, st.expectedTicketCount ?? st.ticketCount) && live.current) {
            done = true;
            clearAllTicketDrafts();
            setTickets(mine);
            setReceipts(res.receipts ?? []);
            setServerNow(res.serverNow);
            setPhase("paid");
            // CHI-27: only now (all of the order's tickets exist) refetch the event ticket hooks.
            notifyTicketsConfirmed(forOrder.map(t => t.eventSlug));
            if (trpcUtilsRef.current) {
              void invalidateConfirmedTicketCache(trpcUtilsRef.current, forOrder.map(t => t.eventSlug), authenticatedViewer)
                .catch(() => undefined);
            }
          }
        }
      } catch {
        /* keep polling */
      } finally {
        inFlight = false;
      }
      schedule();
    };

    const resume = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        if (timer) { clearTimeout(timer); timer = undefined; }
        return;
      }
      if (!timer && !inFlight) void check();
    };

    (async () => {
      try {
        const pay = await api.verifyCheckoutReturn({ orderId: returnOrderId as number, sessionId });
        if (!live.current) return;
        orderId = pay.orderId;
        if (pay.paymentStatus === "unpaid") {
          done = true;
          setPhase("unpaid");
          try {
            const order = await api.ticketOrderForRestore({ orderId: pay.orderId });
            if (order && live.current) setBackHref(`/events/${encodeURIComponent(order.eventSlug)}/tickets?checkout=cancelled&order=${pay.orderId}`);
          } catch { /* keep the calendar link */ }
          return;
        }
        // A Stripe return/session lookup is not the fulfillment webhook.
        setPhase("checking");
        started = Date.now();
        await check();
      } catch (error) {
        // NOT_FOUND (not this buyer's session): generic notice + normal view. Network: normal view.
        if (!live.current) return;
        done = true;
        setPhase(isNotFound(error) ? "not_found" : "idle");
        void load();
      }
    })();

    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    return () => {
      live.current = false;
      done = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
    };
  }, [sessionId, returnOrderId, load, pollMs, windowMs, backgroundMs, authenticatedViewer]);

  const showList = (phase === "idle" || phase === "paid" || phase === "unpaid" || phase === "not_found") && tickets.length > 0;
  const canRenderReadyContent = showList && !checkoutCancelled && (phase === "paid" || (phase === "idle" && !sessionId));
  const visibleReceipts = canRenderReadyContent
    ? receipts.filter(receipt => returnOrderId === null || receipt.orderId === returnOrderId)
    : [];
  const missingCurrentReceipt = canRenderReadyContent && returnOrderId !== null &&
    !receipts.some(receipt => receipt.orderId === returnOrderId);
  const stripePaid = paymentConfirmed && (phase === "finalizing" || phase === "timeout" || phase === "paid");
  const ticketGroups = Array.from(tickets.reduce((groups, ticket) => {
    const group = groups.get(ticket.eventSlug) ?? [];
    group.push(ticket);
    groups.set(ticket.eventSlug, group);
    return groups;
  }, new Map<string, MyTicket[]>()).entries());
  return (
    <section className="chi-ticket-page" aria-label="My tickets" style={{ marginTop: "2rem" }}>
      {stripePaid && <p data-testid="payment-received" style={{ color: "#4ADE80", fontWeight: 700 }}>{PAYMENT_RECEIVED_COPY}</p>}
      {phase === "checking" && (
        <div role="status" aria-live="polite">
          <p>Checking your payment…</p>
        </div>
      )}
      {phase === "under_review" && (
        <div role="status"><p>Your order is under review.</p>{supportEmail ? <p>Questions? Contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.</p> : null}</div>
      )}
      {phase === "finalizing" && (
        <div role="status" aria-live="polite">
          <h2 tabIndex={-1}>Finalizing your tickets…</h2>
          <p>We're preparing your tickets and QR codes. We'll also email your ticket details.</p>
        </div>
      )}
      {phase === "not_found" && (
        <div role="status" data-testid="order-not-found">
          <p>{ORDER_NOT_FOUND_COPY}</p>
        </div>
      )}
      {phase === "timeout" && (
        <div role="status" aria-live="polite" data-testid="tickets-timeout">
          <p>{paymentConfirmed ? CHI28_TIMEOUT_COPY : PAYMENT_PENDING_COPY}{supportEmail ? <> Questions? Contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.</> : null}</p>
        </div>
      )}
      {phase === "unpaid" && (
        <div role="status">
          <p>Payment not completed</p>
          <a href={backHref}>Back to tickets</a>
        </div>
      )}
      {phase === "paid" && (
        <div role="status" aria-live="polite">
          <p>You're going! Your tickets are below.</p>
        </div>
      )}
      {showList && (
        <>
          <div className="chi-ticket-groups">
            {ticketGroups.map(([slug, groupTickets], groupIndex) => (
              <section key={slug} className="chi-ticket-group" aria-label={`Tickets for ${titleForSlug?.(slug) ?? "your event"}`}>
                <TicketGroupHeader tickets={groupTickets} eventTitle={titleForSlug?.(slug)} details={detailsForSlug?.(slug)} />
                <ul style={{ display: "grid", gap: "0.75rem", padding: 0, margin: 0 }}>
                  {groupTickets.map((ticket, ticketIndex) => (
                    <li key={ticket.ticketId} style={{ listStyle: "none", minWidth: 0 }}>
                      <TicketQrCard ticket={ticket} eventTitle={titleForSlug?.(slug)} details={detailsForSlug?.(slug)} now={now} onGuestUpdated={() => void load()} allowGuestEdits={allowGuestEdits} includeStyles={groupIndex === 0 && ticketIndex === 0} />
                    </li>
                  ))}
                </ul>
                {visibleReceipts
                  .filter(receipt => receipt.eventSlug === slug && groupTickets.some(ticket => ticket.orderId === receipt.orderId))
                  .map(receipt => <TicketOrderReceipt key={receipt.orderId} receipt={receipt} />)}
              </section>
            ))}
          </div>
          {missingCurrentReceipt && <p className="chi-ticket-receipt__missing">Payment details aren’t available here. View your order for payment details.</p>}
          {canRenderReadyContent ? readyContent?.(tickets) : null}
        </>
      )}
    </section>
  );
}
